import { uiText, useUiLanguage, currentLocale } from '@/i18n/interface';
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Receipt } from 'lucide-react';
import { useToast } from "@/hooks/use-toast";
import { supabase } from '@/integrations/supabase/client';
import { useDojoSettings } from '@/hooks/useDojoSettings';
import { getCurrencySymbol } from '@/utils/currencyUtils';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useCurrencySettings } from '@/hooks/useCurrencySettings';
import { addGreekFontSupport, addTextToPDF, addBilingualTextLine, addBilingualHeader, getBilingualText } from '@/utils/pdfGreekSupport';
import jsPDF from 'jspdf';

interface InvoiceGeneratorProps {
  paymentId: string;
  studentName: string;
  amount: number;
  category: string;
  paymentStatus?: string;
  onInvoiceGenerated?: () => void;
}

const InvoiceGenerator: React.FC<InvoiceGeneratorProps> = ({
  paymentId,
  studentName,
  amount,
  category,
  paymentStatus = 'pending',
  onInvoiceGenerated
}) => {
  useUiLanguage();
  const [isGenerating, setIsGenerating] = useState(false);
  const { toast } = useToast();
  const { settings } = useDojoSettings();
  const { profile } = useAuth();
  const { defaultCurrency, formatCurrency } = useCurrencySettings();
  const { t, i18n } = useTranslation(['payments', 'common']);

  const generateInvoice = async () => {
    setIsGenerating(true);
    try {
      // Validate paymentId first
      if (!paymentId || paymentId === 'undefined') {
        throw new Error(uiText("Invalid payment ID provided"));
      }

      console.log('🧾 Generating invoice for payment ID:', paymentId);

      // First, get the payment instruction to find the correct dojo_id and category
      const { data: paymentInstruction, error: paymentError } = await supabase
        .from('payment_instructions')
        .select('dojo_id, category_name, status')
        .eq('id', paymentId)
        .single();

      if (paymentError) {
        console.error('❌ Error fetching payment instruction:', paymentError);
        throw paymentError;
      }

      console.log('✅ Payment instruction found:', paymentInstruction);

      // Read the saved status on every export so a stale screen cannot print an old status.
      const currentPaymentStatus = paymentInstruction.status || paymentStatus;
      const invoiceStatus = currentPaymentStatus === 'complete' ? 'paid' : currentPaymentStatus;

      // Get the currency from the payment category
      const { data: categoryData, error: categoryError } = await supabase
        .from('payment_categories')
        .select('currency')
        .eq('name', paymentInstruction.category_name)
        .eq('dojo_id', paymentInstruction.dojo_id)
        .single();

      const dynamicCurrency = categoryData?.currency || defaultCurrency;
      console.log('Using currency from payment category:', dynamicCurrency, 'for category:', paymentInstruction.category_name);

      // Create invoice record in database with the correct dojo_id
      const { data: invoice, error } = await supabase
        .from('invoices')
        .insert({
          payment_instruction_id: paymentId,
          amount: amount, // Amount is already in cents from the parent component
          status: invoiceStatus,
          currency: dynamicCurrency,
          due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days from now
          dojo_id: paymentInstruction.dojo_id
        })
        .select()
        .single();

      if (error) throw error;

      // Generate PDF
      const pdf = new jsPDF();

      // Add proper font support
      await addGreekFontSupport(pdf);

      const currencySymbol = getCurrencySymbol(dynamicCurrency);
      const invoiceDate = new Date(invoice.invoice_date).toLocaleDateString(currentLocale());
      const dueDate = new Date(invoice.due_date).toLocaleDateString(currentLocale());
      const amountFormatted = (amount / 100).toFixed(2); // Convert from cents to display amount
      const currentLanguage = i18n.language; // Use current language setting

      let currentY = 30;

      try {
        // Header
        currentY = addTextToPDF(pdf, settings.dojo_name || 'Dojo', 20, currentY, 24, 'bold');
        currentY += 10;

        currentY = addTextToPDF(pdf, 'Martial Arts Training', 20, currentY, 12);

        // Invoice title and number
        const invoiceTitle = getBilingualText('Invoice', currentLanguage);
        addTextToPDF(pdf, invoiceTitle, 150, 30, 28, 'bold');
        //remove the invoice number
        //addTextToPDF(pdf, `#${invoice.invoice_number}`, 150, 45, 14);

        // Invoice details
        currentY = 70;
        const billToText = getBilingualText('Bill To', currentLanguage);
        currentY = addTextToPDF(pdf, billToText, 20, currentY, 12);
        currentY += 15;

        currentY = addTextToPDF(pdf, studentName, 20, currentY, 14);
        currentY += 10;

        const studentText = getBilingualText('Student', currentLanguage);
        currentY = addTextToPDF(pdf, studentText, 20, currentY, 12);

        // Right side details
        const invoiceDetailsText = getBilingualText('Invoice Details', currentLanguage);
        addTextToPDF(pdf, invoiceDetailsText, 120, 70, 12);
        addBilingualTextLine(pdf, 'Invoice Date', invoiceDate, 120, 85, currentLanguage, 12);
        addBilingualTextLine(pdf, 'Due Date', dueDate, 120, 95, currentLanguage, 12);
        addBilingualTextLine(pdf, 'Status', uiText(invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)), 120, 105, currentLanguage, 12);
        addBilingualTextLine(pdf, 'Currency', dynamicCurrency, 120, 115, currentLanguage, 12);

        // Table header
        currentY = 140;
        const descriptionText = getBilingualText('Description', currentLanguage);
        const categoryText = getBilingualText('Category', currentLanguage);
        const amountText = getBilingualText('Amount', currentLanguage);

        addTextToPDF(pdf, descriptionText, 20, currentY, 12);
        addTextToPDF(pdf, categoryText, 80, currentY, 12);
        addTextToPDF(pdf, amountText, 150, currentY, 12);

        // Line under header
        pdf.line(20, 145, 190, 145);

        // Table content
        currentY = 155;
        const trainingFeeText = getBilingualText('Training Fee', currentLanguage);
        addTextToPDF(pdf, trainingFeeText, 20, currentY, 12);
        addTextToPDF(pdf, category, 80, currentY, 12);
        addTextToPDF(pdf, `${currencySymbol}${amountFormatted}`, 150, currentY, 12);

        // Total
        currentY = 180;
        const totalDueText = getBilingualText('Total Due', currentLanguage);
        addTextToPDF(pdf, `${totalDueText}: ${currencySymbol}${amountFormatted}`, 120, currentY, 16, 'bold');

        // Footer
        currentY = 220;
        const thankYouText = getBilingualText('Thank you for training with', currentLanguage);
        currentY = addTextToPDF(pdf, `${thankYouText} ${settings.dojo_name}!`, 20, currentY, 12);
        currentY += 15;

        const paymentReminderText = getBilingualText('Please pay by the due date', currentLanguage);
        currentY = addTextToPDF(pdf, paymentReminderText, 20, currentY, 12);
        currentY += 15;

        addBilingualTextLine(pdf, 'Generated on', new Date().toLocaleDateString(currentLocale()), 20, currentY, currentLanguage, 12);

        // Save the PDF
        pdf.save(`invoice.pdf`);

      } catch (error) {
        console.error('Error generating PDF:', error);
        // Fallback to basic PDF generation
        generateBasicInvoicePDF(pdf, invoice, settings, currencySymbol, invoiceDate, dueDate, amountFormatted, studentName, category, dynamicCurrency);
      }

      toast({
        title: uiText("Success"),
        description: uiText("Invoice generated successfully")
      });

      if (onInvoiceGenerated) {
        onInvoiceGenerated();
      }
    } catch (error: any) {
      console.error('❌ Error generating invoice:', error);

      let errorMessage = uiText("Failed to generate invoice");
      if (error.message) {
        errorMessage = error.message;
      } else if (error.code === 'PGRST116') {
        errorMessage = uiText("Payment record not found. Please refresh and try again.");
      } else if (error.code === 'PGRST200') {
        errorMessage = uiText("Missing payment information. Please contact support.");
      }

      toast({
        title: uiText("Error"),
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Fallback function for basic PDF generation
  const generateBasicInvoicePDF = (pdf: jsPDF, invoice: any, settings: any, currencySymbol: string, invoiceDate: string, dueDate: string, amountFormatted: string, studentName: string, category: string, defaultCurrency: string) => {
    // Basic fallback rendering in English only
    pdf.setFontSize(24);
    pdf.text(settings.dojo_name || 'Dojo', 20, 30);
    pdf.setFontSize(12);
    pdf.text(uiText("Martial Arts Training"), 20, 40);

    pdf.setFontSize(28);
    pdf.text(uiText("INVOICE"), 150, 30);
    pdf.setFontSize(14);
    //remove invoice number
    //pdf.text(`#${invoice.invoice_number}`, 150, 45);

    pdf.setFontSize(12);
    pdf.text(uiText("Bill To:"), 20, 70);
    pdf.setFontSize(14);
    pdf.text(studentName, 20, 85);
    pdf.setFontSize(12);
    pdf.text(uiText("Student"), 20, 95);

    pdf.text(uiText("Invoice Details:"), 120, 70);
    pdf.text(uiText("Invoice Date: {{value0}}", { value0: invoiceDate }), 120, 85);
    pdf.text(uiText("Due Date: {{value0}}", { value0: dueDate }), 120, 95);
    pdf.text(uiText("Status: {{value0}}", { value0: uiText(invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)) }), 120, 105);
    pdf.text(uiText("Currency: {{value0}}", { value0: defaultCurrency }), 120, 115);

    pdf.text(uiText("Description"), 20, 140);
    pdf.text(uiText("Category"), 80, 140);
    pdf.text(uiText("Amount"), 150, 140);

    pdf.line(20, 145, 190, 145);

    pdf.text(uiText("Training Fee"), 20, 155);
    pdf.text(category, 80, 155);
    pdf.text(uiText("{{value0}}{{value1}}", { value0: currencySymbol, value1: amountFormatted }), 150, 155);

    pdf.setFontSize(16);
    pdf.text(uiText("Total Due: {{value0}}{{value1}}", { value0: currencySymbol, value1: amountFormatted }), 120, 180);

    pdf.setFontSize(12);
    pdf.text(uiText("Thank you for training with {{value0}}!", { value0: settings.dojo_name }), 20, 220);
    pdf.text(uiText("Please pay by the due date to avoid late fees."), 20, 235);
    pdf.text(uiText("Generated on: {{value0}}", { value0: new Date().toLocaleDateString(currentLocale()) }), 20, 250);

    pdf.save(`invoice.pdf`);
  };

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={generateInvoice}
      disabled={isGenerating}
      title={uiText("Generate Invoice")}
      className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
    >
      {isGenerating ? (
        <>{uiText("Generating...")}</>
      ) : (
        <>
          <Receipt className="h-4 w-4 mr-1" />
          {uiText("Invoice")}</>
      )}
    </Button>
  );
};

export default InvoiceGenerator;
